/**
 * Reusable input validation utilities for form fields
 * Enforces language-specific input (Farsi, English, Numeric)
 */

import { isFarsiInput, isEnglishInput } from './validation';

/**
 * Field type categories for validation
 */
export const FIELD_CATEGORIES = {
    FARSI_ONLY: 'farsi_only',      // Farsi text fields (name, address, etc.)
    ENGLISH_ONLY: 'english_only',   // English/Latin fields (email, password)
    NUMERIC: 'numeric',              // Numeric fields (mobile, national_id) - accepts Persian/English digits
};

/**
 * Default field mappings (can be overridden per form)
 */
export const DEFAULT_FIELD_MAPPINGS = {
    farsiOnly: ['first_name', 'last_name', 'address'],
    numeric: ['national_id', 'mobile'],
    englishOnly: ['email', 'password', 'password2'],
};

/**
 * Validates input value based on field category
 * @param {string} fieldName - Name of the field
 * @param {string} value - Input value to validate
 * @param {Object} fieldMappings - Custom field mappings (optional)
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
export const validateInputLanguage = (fieldName, value, fieldMappings = {}) => {
    const mappings = {
        farsiOnly: fieldMappings.farsiOnly || DEFAULT_FIELD_MAPPINGS.farsiOnly,
        numeric: fieldMappings.numeric || DEFAULT_FIELD_MAPPINGS.numeric,
        englishOnly: fieldMappings.englishOnly || DEFAULT_FIELD_MAPPINGS.englishOnly,
    };

    // Empty values are considered valid (required validation is separate)
    if (!value || (typeof value === 'string' && !value.trim())) {
        return { isValid: true, errorMessage: '' };
    }

    if (typeof value !== 'string') {
        return { isValid: true, errorMessage: '' };
    }

    // Check Farsi-only fields
    if (mappings.farsiOnly.includes(fieldName)) {
        if (!isFarsiInput(value)) {
            return {
                isValid: false,
                errorMessage: 'لطفاً از فونت فارسی استفاده کنید'
            };
        }
        return { isValid: true, errorMessage: '' };
    }

    // Check numeric fields (accept Persian/English digits)
    if (mappings.numeric.includes(fieldName)) {
        const numericPattern = /^[\u06F0-\u06F9\u0660-\u06690-9\s-]*$/; // Persian, Arabic-Indic, and English digits
        if (!numericPattern.test(value)) {
            return {
                isValid: false,
                errorMessage: 'لطفاً فقط اعداد وارد کنید'
            };
        }
        return { isValid: true, errorMessage: '' };
    }

    // Check English-only fields
    if (mappings.englishOnly.includes(fieldName)) {
        if (!isEnglishInput(value)) {
            return {
                isValid: false,
                errorMessage: 'لطفاً از فونت انگلیسی استفاده کنید'
            };
        }
        return { isValid: true, errorMessage: '' };
    }

    // Default: valid if not in any category
    return { isValid: true, errorMessage: '' };
};

/**
 * Creates a validated handleChange function that blocks invalid input
 * @param {Function} originalHandleChange - Original handleChange function
 * @param {Function} setErrors - Function to set errors state
 * @param {Object} fieldMappings - Custom field mappings (optional)
 * @returns {Function} Validated handleChange function
 */
export const createValidatedHandleChange = (originalHandleChange, setErrors, fieldMappings = {}) => {
    return (fieldName, value) => {
        // Validate input language
        const validation = validateInputLanguage(fieldName, value, fieldMappings);

        // If invalid, set error and prevent update
        if (!validation.isValid) {
            setErrors(prev => ({
                ...prev,
                [fieldName]: validation.errorMessage
            }));
            return; // Block invalid input
        }

        // Clear error if input is valid
        setErrors(prev => {
            const next = { ...prev };
            delete next[fieldName];
            return next;
        });

        // Call original handleChange if validation passes
        originalHandleChange(fieldName, value);
    };
};

/**
 * Validates form data for language constraints (for form submission)
 * @param {Object} formData - Form data object
 * @param {Array} fields - Array of field configurations
 * @param {Object} fieldMappings - Custom field mappings (optional)
 * @returns {Object} { errors: Object, isValid: boolean }
 */
export const validateFormLanguage = (formData, fields, fieldMappings = {}) => {
    const errors = {};
    const mappings = {
        farsiOnly: fieldMappings.farsiOnly || DEFAULT_FIELD_MAPPINGS.farsiOnly,
        numeric: fieldMappings.numeric || DEFAULT_FIELD_MAPPINGS.numeric,
        englishOnly: fieldMappings.englishOnly || DEFAULT_FIELD_MAPPINGS.englishOnly,
    };

    fields.forEach(field => {
        if (field.condition && !field.condition(formData)) {
            return; // Skip validation for conditional fields that aren't shown
        }

        const value = formData[field.name];
        if (!value || (typeof value === 'string' && !value.trim())) {
            return; // Skip empty values (required validation is separate)
        }

        // Validate Farsi fields
        if (mappings.farsiOnly.includes(field.name)) {
            if (field.name === 'address') {
                // Address can have numbers (converted to English)
                const addressPattern = /^[\u0600-\u06FF\uFB8A-\uFEEF\uFB50-\uFDFF\u200C\s0-9]*$/;
                if (!addressPattern.test(value)) {
                    errors[field.name] = 'لطفاً از فونت فارسی استفاده کنید';
                }
            } else {
                // Name fields should be Farsi
                if (!isFarsiInput(value) && !/^[0-9\s]*$/.test(value)) {
                    errors[field.name] = 'لطفاً از فونت فارسی استفاده کنید';
                }
            }
        }

        // Validate numeric fields
        if (mappings.numeric.includes(field.name)) {
            const numericPattern = /^[\u06F0-\u06F9\u0660-\u06690-9\s-]*$/;
            if (!numericPattern.test(value)) {
                errors[field.name] = 'لطفاً فقط اعداد وارد کنید';
            }
        }

        // Validate English fields
        if (mappings.englishOnly.includes(field.name)) {
            if (!isEnglishInput(value)) {
                errors[field.name] = 'لطفاً از فونت انگلیسی استفاده کنید';
            }
        }
    });

    return {
        errors,
        isValid: Object.keys(errors).length === 0
    };
};

